import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserCog, UsersRound } from "lucide-react";
import { api } from "@/api";
import { C } from "@/theme";
import { Badge, Button, Card, SectionTitle, Picker, LoadingState, ErrorState, EmptyState } from "@/ui";
import { PageHeader } from "@/components/AppShell";

export default function Assignments() {
  const qc = useQueryClient();
  const [rider, setRider] = useState("");
  const [trainer, setTrainer] = useState("");
  const [error, setError] = useState("");

  const aQ = useQuery({ queryKey: ["assignments"], queryFn: () => api.get("/assignments") });
  const ridersQ = useQuery({ queryKey: ["riders"], queryFn: () => api.get("/riders") });
  const trainersQ = useQuery({ queryKey: ["trainers"], queryFn: () => api.get("/trainers") });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["assignments"] });

  const add = useMutation({ mutationFn: () => api.post("/assignments", { rider_id: rider, trainer_id: trainer }), onSuccess: () => { setRider(""); setTrainer(""); setError(""); invalidate(); }, onError: (e) => setError(e.message) });
  const remove = useMutation({ mutationFn: (id) => api.del(`/assignments/${id}`), onSuccess: invalidate });

  const grouped = useMemo(() => {
    const map = {};
    (aQ.data ?? []).forEach((a) => { if (!map[a.trainer_id]) map[a.trainer_id] = { trainer_name: a.trainer_name ?? "Unknown", items: [] }; map[a.trainer_id].items.push(a); });
    return Object.entries(map).sort((x, y) => x[1].trainer_name.localeCompare(y[1].trainer_name));
  }, [aQ.data]);

  return (
    <div>
      <PageHeader title="Tutor Assignments" subtitle="Who's tutoring whom" />
      {aQ.isLoading ? <LoadingState /> : aQ.isError ? <ErrorState onRetry={aQ.refetch} /> : (
        <div className="flex flex-col gap-4 max-w-3xl">
          <Card>
            <SectionTitle>Pair a Rider with a Tutor</SectionTitle>
            <div className="mt-3 grid md:grid-cols-2 gap-3">
              <Picker label="Rider" testID="assign-rider-picker" placeholder="Select rider" searchable value={rider} onSelect={setRider} options={(ridersQ.data ?? []).map((r) => ({ key: r.id, label: r.name }))} />
              <Picker label="Tutor" testID="assign-trainer-picker" placeholder="Select tutor" value={trainer} onSelect={setTrainer} options={(trainersQ.data ?? []).map((t) => ({ key: t.id, label: t.name }))} />
            </div>
            {error ? <p style={{ color: C.error }} className="text-[13px] font-semibold mt-2">{error}</p> : null}
            <div className="mt-3 w-48"><Button label="Create Pairing" testID="assign-submit" disabled={!rider || !trainer} loading={add.isPending} onClick={() => add.mutate()} /></div>
          </Card>
          {grouped.length === 0 ? <EmptyState icon={<UsersRound size={44} color={C.borderStrong} />} title="No assignments yet" subtitle="Pair a rider with a tutor above" /> : grouped.map(([tid, group]) => (
            <Card key={tid} testID={`assign-group-${tid}`}>
              <div className="flex items-center gap-2.5"><UserCog size={20} color={C.brandPrimary} /><span style={{ color: C.onSurface }} className="font-extrabold flex-1">{group.trainer_name}</span><Badge label={`${group.items.length} rider${group.items.length === 1 ? "" : "s"}`} variant="brand" /></div>
              <div className="divide-y mt-2" style={{ borderColor: C.divider }}>{group.items.map((a) => (<div key={a.id} className="flex items-center gap-3 py-2"><div style={{ background: C.brandTertiary }} className="w-9 h-9 rounded-full flex items-center justify-center"><span style={{ color: C.onBrandTertiary }} className="text-sm font-extrabold">{a.rider_name?.charAt(0)}</span></div><span style={{ color: C.onSurface }} className="text-[15px] font-bold flex-1">{a.rider_name}</span><Button label="Remove" variant="ghost" small full={false} testID={`assign-remove-${a.id}`} onClick={() => remove.mutate(a.id)} /></div>))}</div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
